/**
 * THE OVERVIEW'S "COMING UP" CARD — what still falls due, and how far ahead it
 * looks.
 *
 * HOW FAR AHEAD IS NOT DECIDED HERE. It asks the one shared next-month rule
 * (nextMonthWindow, the rule behind the Expenses page's "incl. next month"),
 * so the month this card names and the figure beside it cannot disagree. It
 * used to work out its own "end of next month", which in December named
 * January over a figure that already left January out: a December withdrawal
 * cannot cover a January cost. (ZEN-726)
 *
 * THE WORDING IS MARYSE'S, approved 2026-10-13 — change it only with her
 * approval. December's last sentence is written as best practice, not as a rule.
 */
import { useQuery } from "@tanstack/react-query";
import { Bank, ClockCountdown } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Card, Btn } from "@/components/common";
import { localISO, money } from "@/lib/format";
import { readyToWithdrawNowUsd, comingUpUsd, newMoneyUsd } from "@/lib/withdrawalHeadroom";
import { nextMonthWindow } from "@/lib/withdrawThisAmount";
import { useStudentWithdrawals, editWithdrawalNavState } from "@/lib/untiedWithdrawals";
import { tieFirstPlan, withdrawLeftNavState, comingUpWords, nextStepWords, tieButtonWords, taxYearOf } from "@/lib/tieFirst";

export const COMING_UP_ADVICE = "Time a withdrawal so the funds are ready before they hit.";
export const COMING_UP_DECEMBER_ADVICE = "January's costs belong to the next tax year, so it is best to withdraw for them in January.";

/**
 * The last day the card counts, the month that day is in, and the sentence that
 * closes the card. The shared rule hands back the first day it does NOT count;
 * the day before it is the last one it does.
 */
export function comingUpWindow(todayISO) {
  const { endISO, addsNextMonth } = nextMonthWindow(todayISO);
  const last = new Date(Number(endISO.slice(0, 4)), Number(endISO.slice(5, 7)) - 1, 0);
  return {
    throughISO: localISO(last),
    monthName: last.toLocaleDateString(undefined, { month: "long" }),
    advice: addsNextMonth ? COMING_UP_ADVICE : COMING_UP_DECEMBER_ADVICE,
  };
}

/** What is due by the end of next month for one child, and the plan for the
 *  money already withdrawn but tied to nothing (ZEN-791). Shared by this card
 *  and the Safe to withdraw card, on one cached request each. */
export function useComingUp(studentId) {
  const todayISO = localISO(new Date());
  const { throughISO, monthName, advice } = comingUpWindow(todayISO);
  // Two dates, two jobs, both needed (ZEN-198): as_of_date fixes the
  // exchange-rate basis; distribution_date is the day the money would come out,
  // which is what lets the server leave out a cost from another calendar year
  // and keep the ones not yet paid. Today, because the card describes money
  // drawn now.
  const { data: upcoming } = useQuery({
    queryKey: ["coming-up", studentId, throughISO, todayISO],
    queryFn: async () => (await api.get(`/withdrawals/eligible-preview?student_id=${studentId}&as_of_date=${throughISO}&distribution_date=${todayISO}`)).data,
    enabled: !!studentId,
  });
  // Until this list has loaded, nobody knows whether money already withdrawn
  // covers part of the need, so the card offers no withdraw button at all
  // (ZEN-791 review): a slow or failed load must never show the full amount.
  const { data: withdrawals = [], isSuccess: withdrawalsKnown } = useStudentWithdrawals(studentId);
  // ONE TAX YEAR, FOR EVERY FIGURE ON THIS CARD (ZEN-796 review, round 3). The
  // window never leaves the calendar year it ends in (nextMonthWindow stops at
  // 31 December), so that is the tax year shown, and only its expenses count —
  // by the same shared test the Expenses page uses. The server already refuses
  // a cost paid in another calendar year; this keeps the card from depending
  // on that alone.
  const year = Number(throughISO.slice(0, 4));
  const items = (upcoming?.items || []).filter((it) => it && taxYearOf(it) === year);
  // Both figures count what the withdrawal screen would actually let her move,
  // through the one shared rule in lib/withdrawalHeadroom.js. (ZEN-198)
  const readyNow = readyToWithdrawNowUsd(items);
  const comingNext = comingUpUsd(items, throughISO);
  // Only the expenses that add to that figure, so "expense" is singular when
  // one does.
  const dueCount = items.filter((it) => it.is_future
    && String(it.transaction_date || "").slice(0, 10) <= throughISO && newMoneyUsd(it) > 0.005).length;
  // Incurred first, then what falls due (ZEN-796); same child, same tax year.
  const plan = tieFirstPlan({ incurred: readyNow, due: comingNext, withdrawals, studentId, year });
  return { readyNow, comingNext, dueCount, monthName, advice, plan, withdrawalsKnown };
}

/** Opens the one untied withdrawal for tying, or the list when there are several. */
export const tieUntiedNav = (nav, plan) =>
  (plan.rows.length === 1 ? nav("/withdrawals", { state: editWithdrawalNavState(plan.rows[0].id) }) : nav("/withdrawals"));

/** Amounts in bold, words as they are. */
const Words = ({ parts }) => parts.map((p, i) => (typeof p === "string" ? p
  : <b key={i} className={`tnum${p.green ? " text-[#077A39]" : ""}`}>{p.b}</b>));

export function ComingUpCard({ studentId, onWithdraw, nav }) {
  const coming = useComingUp(studentId);
  if (!(coming.comingNext > 0.005)) return null;
  return (
    <Card className="border-[#034AA2] bg-[#034AA2]/5" data-testid="coming-up-card">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <ClockCountdown size={26} weight="bold" className="mt-0.5 shrink-0 text-[#034AA2]" />
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wide text-[#034AA2]">Coming up</h3>
            <ComingUpWords coming={coming} />
          </div>
        </div>
        <ComingUpButtons coming={coming} nav={nav} onWithdraw={onWithdraw} />
      </div>
    </Card>
  );
}

/** The Next step part of the Overview's combined "529 Withdrawals" card
 *  (ZEN-814): the Coming up box's sentence and buttons under one heading,
 *  with Maryse's own sentence in the situation she wrote it for. */
export function NextStepSection({ coming, onWithdraw, nav }) {
  if (!(coming.comingNext > 0.005)) return null;
  // Keeps the Coming up box's test name: it IS that content, moved here, and
  // the browser checks of its figures (withdrawal-screen-truthfulness) read it.
  return (
    <div className="mt-3 border-t border-[#C2C9D1] pt-3" data-testid="coming-up-card">
      <div className="text-xs font-bold uppercase tracking-wide" data-testid="next-step-heading">Next step · by the end of {coming.monthName}</div>
      <ComingUpWords coming={coming} combined />
      <div className="mt-2"><ComingUpButtons coming={coming} nav={nav} onWithdraw={onWithdraw} combined /></div>
    </div>
  );
}

function ComingUpWords({ coming, combined = false }) {
  const { readyNow, comingNext, monthName, advice, plan } = coming;
  // Every sentence about the untied money comes from lib/tieFirst.js (ZEN-791,
  // ZEN-796, ZEN-814); with none untied the card keeps its own approved wording.
  const words = plan && (combined ? nextStepWords : comingUpWords)(plan, monthName, money);
  return (
    <>
            {words ? (
              <p className="mt-1 text-sm text-[#1f2937]" data-testid="coming-up-sentence"><Words parts={words.sentence} /></p>
            ) : (
              <p className="mt-1 text-sm text-[#1f2937]" data-testid="coming-up-sentence">529-eligible costs due between now and the end of {monthName} total <b className="tnum">{money(comingNext, "USD")}</b>. {advice}</p>
            )}
            {words ? (
              words.readyLine && <p className="mt-0.5 text-xs text-[#6b7280]" data-testid="coming-up-ready-now"><Words parts={words.readyLine} /></p>
            ) : (
              readyNow > 0.005 && <p className="mt-0.5 text-xs text-[#6b7280]" data-testid="coming-up-ready-now">You also have <b className="tnum text-[#077A39]">{money(readyNow, "USD")}</b> already incurred and ready to withdraw now.</p>
            )}
    </>
  );
}

function ComingUpButtons({ coming, nav, onWithdraw, combined = false }) {
  const { monthName, plan, withdrawalsKnown } = coming;
  const leftToWithdraw = plan && !plan.covers;
  const tieLabel = plan && (combined ? nextStepWords(plan, monthName, money).tieLabel : tieButtonWords(plan, money));
  return plan && nav ? (
    <div className="flex flex-wrap gap-2">
      <Btn onClick={() => tieUntiedNav(nav, plan)} data-testid="coming-up-tie-first">{tieLabel}</Btn>
      {leftToWithdraw && (
        <Btn onClick={() => nav("/withdrawals", { state: withdrawLeftNavState(plan, monthName) })} data-testid="coming-up-withdraw-left"><Bank size={16} weight="bold" /> Withdraw {money(plan.left, "USD")}</Btn>
      )}
    </div>
  ) : withdrawalsKnown ? (
    <Btn onClick={onWithdraw} data-testid="coming-up-cta"><Bank size={16} weight="bold" /> Withdraw &amp; tie expenses</Btn>
  ) : null;
}
